import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageMeta } from '../../utils/pageMeta';
import { scrollToChild, scrollToY } from '../../utils/scrollHelpers';
import { getKvizPitanjaForLekcijaByLekcijaId, getLekcijaById, getPitanjaByLekcijaId } from '../../server/server-kategorije-tree';
import MDEditor from '@uiw/react-md-editor';
import { mathPlugins } from '../../utils/markdownMath';
import '../../style/lekcija.css';
import '../../style/markdown.css';
import { useUser } from '../../auth/UseUser';
import PitanjeCard from './pitanje/PitanjeCard';
import { TypeOfEtity } from '../../model/enum/typeOfEntity';
import { FaQuestion } from 'react-icons/fa';
import TestoviZaLekciju from './pitanje/TestoviZaLekciju';
import { Oval } from 'react-loader-spinner';
import { MarkDownView } from '../common/MarkDownView';
import AdSlot from '../common/AdSlot';
import AnswerMarkdown from '../common/AnswerMarkdown';

const LekcijaObrazovanjeHome = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { selectedPitanje, setSelectedPitanje, navLista, setNavLista, returnedList, setReturnedList, setTipEtiteta } = useUser();
    const [lekcija, setLekcija] = useState<any>(null);
    usePageMeta(lekcija?.naziv, lekcija?.opis, lekcija?.podoblast?.oblast?.naziv);
    const [kvizPitanja, setKvizPitanja] = useState<any>([]);
    const [datiOdgovori, setDatiOdgovori] = useState<any>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOdgovorIndex, setSelectedOdgovorIndex] = useState(-1);
    const [processing, setProcessing] = useState(false);
    const [brojTacnihOdgovora, setBrojTacnihOdgovora] = useState(0);
    const [mode, setMode] = useState('lekcija');
    const [surveyOdgovori, setSurveyOdgovori] = useState<any>([]);
    const [showResults, setShowResults] = useState(false);
    const scrollViewRef = useRef<any>(null);
    const stopRef = useRef<any>(null);
    const [loading, setLoading] = useState(true);
    const [loadingKvizPitanja, setLoadingKvizPitanja] = useState(false);
    const parentRef = useRef<any>(null);

    useEffect(() => {
        if (!!id) loadLekcija();
    }, [id]);

    useEffect(() => {
        scrollToChild(parentRef.current, scrollViewRef.current, 'smooth');
    }, [selectedPitanje]);

    useEffect(() => {
        setSelectedOdgovorIndex(-1);
    }, [currentIndex]);

    // Kada se startuje kviz (ili se prikaže rezultat), skrol kontejner ide na vrh, a ne ostaje na poziciji iz lekcije.
    useEffect(() => {
        if (mode !== 'test' && mode !== 'rezultat') return;
        const parent = parentRef.current;
        // Na mobilnom je zaglavlje lekcije visoko pa traka sa dugmetom "Prekini" mora biti vidljiva odmah, da se zna da ispod ima sadržaja.
        const traka = document.getElementById('kviz-traka');
        if (traka && window.innerWidth < 768) {
            scrollToChild(parent, traka, 'auto', 8);
        } else {
            scrollToY(parent, 0);
        }
    }, [mode]);

    const loadLekcija = async () => {
        setLoading(true);
        const resp = await getLekcijaById(id);
        if (resp.status == 200) {
            const item = resp.data;
            setLekcija(item);
            let newList: any[] = [];
            if (item.podoblast.oblast.kategorija) newList.push(item.podoblast.oblast.kategorija);
            if (item.podoblast.oblast.podkategorija) newList.push(item.podoblast.oblast.podkategorija);
            if (item.podoblast.oblast.skupOblasti) newList.push(item.podoblast.oblast.skupOblasti);
            if (item.podoblast.oblast) newList.push(item.podoblast.oblast);
            if (item.podoblast) newList.push(item.podoblast);
            newList.push(item);
            setNavLista(newList);
            setTipEtiteta(TypeOfEtity.Pitanje);
            setReturnedList(resp.data.pitanja);
            setLoading(false);
            loadKvizPitanja(id);
            setSelectedPitanje(null);
            //if (resp.data.pitanja?.length !== 0) setSelectedPitanje(resp.data.pitanja.sort((a: any, b: any) => a.redosled - b.redosled)[0]);
        } else {
            console.log('lekcija by id error', resp.data);
            setLoading(false);
        }
    };

    const loadPitanja = async (id: string | undefined) => {
        const resp = await getPitanjaByLekcijaId(id);
        if (resp.status === 200) {
            setTipEtiteta(TypeOfEtity.Pitanje);
            setReturnedList(resp.data);
            setLoading(false);
        } else {
            console.log('lekcija pitanja error', resp.data);
            setLoading(false);
        }
    };

    const loadKvizPitanja = async (id: string | undefined) => {
        setLoadingKvizPitanja(true);
        const resp = await getKvizPitanjaForLekcijaByLekcijaId(id);
        if (resp.status === 200) {
            setKvizPitanja(resp.data?.sort((a: any, b: any) => a.redosled - b.redosled));
            setLoadingKvizPitanja(false);
        } else {
            console.log('lekcija kviz pitanja error', resp.data);
            setLoadingKvizPitanja(false);
        }
    };

    const openTestZnanja = () => {
        setCurrentIndex(0);
        setDatiOdgovori([]);
        if (surveyMode) {
            const lista: (null | any)[] = Array(kvizPitanja.length).fill(null);
            setSurveyOdgovori(lista);
        }
        setBrojTacnihOdgovora(0);
        setMode('test');
        setShowResults(false);
    };

    const closeTestZnanja = () => {
        setCurrentIndex(0);
        setDatiOdgovori([]);
        setBrojTacnihOdgovora(0);
        setMode('lekcija');
        setShowResults(false);
    };

    const scrollToStop = () => scrollToChild(parentRef.current, stopRef.current, 'smooth');

    const scrollToTop = () => scrollToY(parentRef.current, 100, 'smooth');

    const closeSurvey = () => {
        if (surveyMode) {
            setShowResults(true);
        } else {
            openResults();
        }

        scrollToTop();
    };

    const openResults = () => {
        setMode('rezultat');
    };

    const handleOdgovor = (odgovor: any) => {
        setProcessing(true);
        setDatiOdgovori([...datiOdgovori, odgovor]);
        if (odgovor.tacan) setBrojTacnihOdgovora((prev: any) => prev + 1);
        setTimeout(() => {
            if (currentIndex + 1 < kvizPitanja.length) {
                setCurrentIndex((prev: any) => prev + 1);
            } else {
                openResults();
            }
            setProcessing(false);
        }, 800);
    };

    // Test se prikazuje kao survey (sva pitanja na jednoj strani, sa rezultatima) kada je lekcija test (isSurvey)
    // ili kada ima pitanja koja nisu Single (Multi, Connect), jer ih samo survey prikaz ume da prikaže.
    const surveyMode = useMemo(() => !!lekcija?.isSurvey || (kvizPitanja || []).some((p: any) => p?.vrsta && p.vrsta !== 'Single'), [lekcija, kvizPitanja]);

    const brojNenullElemenata = useMemo(() => {
        return surveyOdgovori.filter((item: any) => item !== null).length;
    }, [surveyOdgovori]);

    const ukupnoBodova = useMemo(() => {
        return surveyOdgovori.reduce((zbir: any, odgovor: any) => {
            return zbir + (odgovor?.brojBodova || 0);
        }, 0);
    }, [surveyOdgovori]);

    return (
        <div className="flex h-full flex-col items-center bg-blueDark pt-5">
            <div className={`flex w-full h-full flex-col items-center    `}>
                <div className=" w-full h-full max-w-[1100px] px-[10px] overflow-y-auto gray-small-scroll pb-5 " ref={parentRef}>
                    {mode === 'test' && (
                        <TestoviZaLekciju
                            closeSurvey={closeSurvey}
                            closeTestZnanja={closeTestZnanja}
                            currentIndex={currentIndex}
                            datiOdgovori={datiOdgovori}
                            handleOdgovor={handleOdgovor}
                            kvizPitanja={kvizPitanja}
                            lekcija={lekcija}
                            surveyMode={surveyMode}
                            setSurveyOdgovori={setSurveyOdgovori}
                            showResults={showResults}
                            surveyOdgovori={surveyOdgovori}
                            ukupnoBodova={ukupnoBodova}
                        />
                    )}
                    {mode === 'lekcija' && (
                        <div className="w-full min-h-full  bg-white flex flex-col pt-[30px] pb-[60px] rounded-[10px]">
                            {!loading ? (
                                <div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className=" bg-schoolBoardDark rounded-r-lg p-3  ">
                                            <p className={`text-white text-[16px] lg:text-[24px] font-medium lg:font-semibold `}>{lekcija?.naziv}</p>
                                        </div>
                                        <div className=" bg-greyMedium rounded-l-lg p-3 max-md:[&_*]:!leading-[18px] max-md:[&_p]:!my-0">
                                            <MarkDownView
                                                source={lekcija?.opis}
                                                nazivKlase="lekcija-opis"
                                                style={{ color: 'white', textAlign: 'left', backgroundColor: 'transparent' }}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-5 px-5">
                                        {lekcija?.pitanja
                                            ?.sort((a: any, b: any) => a.redosled - b.redosled)
                                            .map((pitanje: any, index: number) => (
                                                <PitanjeCard pitanje={pitanje} scrollViewRef={scrollViewRef} key={index} index={index} />
                                            ))}
                                    </div>
                                    {!!kvizPitanja.length && !loadingKvizPitanja && (
                                        <div className="grid grid-cols-12 gap-2">
                                            <div className="col-span-11 bg-schoolBoardDark rounded-r-full p-3 flex flex-row items-center justify-between">
                                                <p className="lekcija-naziv tracking-tight">
                                                    {lekcija?.isSurvey ? 'Test zadaci' : 'Kviz zadaci za proveru znanja'}
                                                </p>
                                                <button onClick={openTestZnanja} className="start-button">
                                                    Start
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {loadingKvizPitanja && (
                                        <div className="w-full flex-1 flex items-center justify-center">
                                            <Oval />{' '}
                                        </div>
                                    )}
                                    <AdSlot placement="lekcija" className="px-5" />
                                </div>
                            ) : (
                                <div className="w-full flex-1 flex items-center justify-center">
                                    <Oval />
                                </div>
                            )}
                        </div>
                    )}

                    {mode === 'rezultat' && (
                        <div className="w-full min-h-full  bg-grey-100 flex flex-col py-[30px] rounded-[10px]">
                            <div className="w-full grid grid-cols-9 gap-5">
                                <div className="col-span-8 md:col-span-5 bg-greyMedium rounded-r-full p-3 flex flex-row items-center justify-between">
                                    <p className="lekcija-naziv ">Kviz je završen</p>
                                    <button onClick={closeTestZnanja} className="start-button">
                                        Zatvori
                                    </button>
                                </div>

                                <div className="col-span-9 md:col-span-4 flex flex-row gap-3  items-center justify-center">
                                    <p className="text-greyMedium text-[20px] leading-6 font-bold">Vaša ocena je </p>
                                    <div className="h-[60px] min-w-[60px] border border-greyStrong rounded-full justify-center items-center flex shadow-xl">
                                        <p
                                            className="text-greyStrong text-[40px] leading-[48px] font-bold text-center "
                                            style={{ fontFamily: 'RubikDoodleShadow' }}
                                        >
                                            {Math.max(Math.round((brojTacnihOdgovora / kvizPitanja.length) * 5), 1)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="py-10 px-3 md:px-20 ">
                                <p className="text-greyStrong text-[20px] leading-6 font-bold">
                                    Ispod se nalaze rezultati testa znanja sa tačnim odgovorima i objašnjenjima
                                </p>
                                <div className="flex flex-col mt-5 gap-8">
                                    {kvizPitanja.map((pitanje: any, index: number) => {
                                        return (
                                            <KvizPitanjeCard
                                                pitanje={pitanje}
                                                kvizPitanja={kvizPitanja}
                                                datiOdgovori={surveyMode ? surveyOdgovori : datiOdgovori}
                                                index={index}
                                                key={pitanje.id}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LekcijaObrazovanjeHome;

const KvizPitanjeCard = ({ pitanje, kvizPitanja, datiOdgovori, index }: { pitanje: any; kvizPitanja: any; datiOdgovori: any; index: number }) => {
    const getTacanOdgovor = () => {
        let tacanOdgovor = {};
        pitanje.odgovori.forEach((odgovor: any) => {
            if (odgovor?.tacan) tacanOdgovor = odgovor;
        });
        return tacanOdgovor;
    };

    const isCorrectAnswer = datiOdgovori[index]?.tacan;
    const tacanOdgovor: any = getTacanOdgovor();
    return (
        <div className={`rounded-[15px] pt-3 ${isCorrectAnswer ? 'bg-greenA200' : 'bg-redError'} shadow-xl`}>
            <p className="text-grey-900 text-[24px] leading-7 font-bold ml-10 " style={{ fontFamily: 'RubikDoodleShadow' }}>
                {index + 1}/{kvizPitanja.length}
            </p>
            <div className="bg-white flex flex-col gap-3 rounded-[15px] px-7 py-3 mt-2">
                <div className="flex flex-row items-center gap-5">
                    <div className="h-[60px] min-w-[60px] border border-blueMedium rounded-full justify-center items-center flex">
                        <FaQuestion size={35} color="#234b98" />
                    </div>
                    <MDEditor.Markdown {...mathPlugins} className="test-znanja-naziv" source={pitanje?.naziv} />
                </div>
                <div className="flex flex-row gap-1">
                    <MDEditor.Markdown {...mathPlugins} className="test-znanja-odgovor" source={'<b>Tačan odgovor</b>: ' + tacanOdgovor?.text} />
                </div>
                <div className="flex flex-row gap-1">
                    <MDEditor.Markdown {...mathPlugins} className="test-znanja-opis" source={'<b>Opis</b>: ' + pitanje?.opis} />
                </div>
            </div>
        </div>
    );
};

export const Odgovor = ({ odgovor, handleOdgovor, currentIndex, datiOdgovori }: { odgovor: any; handleOdgovor: any; currentIndex: any; datiOdgovori: any }) => {
    const [selected, setSelected] = useState(false);
    const selectOdgovor = (odgovor: any) => {
        setSelected(true);
        handleOdgovor(odgovor);
    };

    useEffect(() => {
        setSelected(false);
    }, [odgovor]);

    return (
        <button
            className={`w-full max-w-[450px] min-h-[60px] ${
                !selected && currentIndex + 1 === datiOdgovori.length && odgovor.tacan
                    ? 'bg-greenA200'
                    : !selected
                      ? 'bg-blueLight hover:bg-blueHoverLight'
                      : selected && odgovor.tacan
                        ? 'bg-greenA200'
                        : 'bg-redError'
            } flex flex-row items-center gap-5 px-6 py-2 rounded-[15px] transition`}
            onClick={() => selectOdgovor(odgovor)}
        >
            <AnswerMarkdown source={odgovor.text} />
        </button>
    );
};
